import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import BinaryBase64Tool from '../components/BinaryBase64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I convert binary to Base64?', a: 'Paste a string of 0s and 1s above (spaces are ignored). The tool groups it into 8-bit bytes and re-encodes those bytes as Base64.' },
  { q: 'Why must the binary string be a multiple of 8 digits?', a: 'Base64 (like all byte-oriented encodings) operates on whole bytes, and each byte is 8 bits. A binary string that is not a multiple of 8 cannot be split into complete bytes.' },
  { q: 'What is the difference between binary and Base64?', a: 'Binary represents each byte as 8 characters (0s and 1s); Base64 packs three bytes into four characters. Base64 is roughly 8 times more compact than a raw binary-digit string.' },
  { q: 'Can I convert Base64 back to binary?', a: 'Yes — switch the direction toggle to Base64 → Binary to get the binary digit representation of the same bytes.' },
];

export default function BinaryToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="Binary to Base64 Converter — and Back | EasyBase64"
        description="Convert a binary (0/1) string to Base64 and Base64 back to binary online for free. Works on raw bytes, instant and private — runs entirely in your browser."
        path="/binary-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'Binary to Base64 Converter', description: 'Convert binary digit strings to Base64 and back in the browser.', path: '/binary-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Binary to Base64', path: '/binary-to-base64' }]),
        ]}
      />
      <PageHero
        title="Binary to Base64"
        subtitle={<>Convert a binary digit string to Base64 and back, byte for byte. Working in hex instead? Try <Link to="/hex-to-base64" className="underline">Hex to Base64</Link>.</>}
      />
      <div className="mb-12">
        <BinaryBase64Tool />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Binary vs. Base64</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed`}>
          A raw binary-digit string spends 8 characters per byte. Base64 packs three bytes into four
          characters, making it about 8 times more compact — which is why binary digit strings are
          used for teaching and debugging, while Base64 is used for actually transmitting or storing
          data as text.
        </p>
      </section>
      <FaqSection title="Binary to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
